import { useCallback, useEffect, useState } from "react";
import { LayoutDashboard, ScrollText, DatabaseBackup, Monitor, Wifi, PlugZap, Users, History, LifeBuoy, Loader2, Palette, KeyRound, Workflow } from "lucide-react";
import { useOutletContext } from "react-router-dom";
import { Branding } from "@/components/Branding";
import { DisplaySettings } from "@/components/DisplaySettings";
import { AutomationPanel } from "@/pages/Automation";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, fmtDate } from "@/lib/api";

const SECTIONS = [
  { id: "overview", label: "Vue d'ensemble", icon: LayoutDashboard },
  { id: "audit", label: "Journal d'audit", icon: ScrollText },
  { id: "automation", label: "Automatisation", icon: Workflow, module: "automation" },
  { id: "maintenance", label: "Maintenance", icon: DatabaseBackup },
  { id: "branding", label: "Personnalisation", icon: Palette, module: "branding" },
];

const ACTION_LABEL = {
  login: "Connexion", user_created: "Compte créé", user_deleted: "Compte supprimé",
  machine_deleted: "Machine supprimée", enrollment_regenerated: "Clé régénérée",
  support_created: "Assistance créée", support_ended: "Assistance terminée",
  retention_updated: "Conservation modifiée", automation_updated: "Automatisation modifiée", agent_uninstall_requested: "Désinstallation demandée", agent_uninstalled: "Agent désinstallé", agent_update: "Mise à jour de l'agent", help_requested: "Demande d'aide", update_policy: "Mises à jour automatiques", digest_sent: "Résumé quotidien envoyé", client_report_sent: "Rapport client envoyé", command: "Commande exécutée", branding_updated: "Personnalisation",
};

const Stat = ({ icon: Icon, label, value, id, tone = "text-blue-600" }) => (
  <div className="rounded-lg border border-slate-200 p-5">
    <Icon className={`h-5 w-5 ${tone}`} />
    <p className="text-3xl font-semibold mt-3" data-testid={id}>{value}</p>
    <p className="text-sm text-slate-500">{label}</p>
  </div>
);

const Overview = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/overview").then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!d) return <Loader2 className="h-6 w-6 animate-spin text-slate-400" />;
  const saturated = d.active_sessions >= d.licensed;
  const unlimited = d.licensed >= 999;
  return (
    <div className="space-y-6" data-testid="admin-overview">
      {!unlimited && <div className={`rounded-lg border p-5 flex flex-wrap items-center gap-x-8 gap-y-3 ${saturated ? "border-amber-300 bg-amber-50" : "border-slate-200"}`} data-testid="licence-usage">
        <div className="flex items-center gap-3">
          <KeyRound className={`h-6 w-6 ${saturated ? "text-amber-600" : "text-blue-600"}`} />
          <div>
            <p className="text-2xl font-semibold" data-testid="licence-usage-value">{d.active_sessions} / {d.licensed}</p>
            <p className="text-sm text-slate-500">Prises en main en cours / licences</p>
          </div>
        </div>
        <p className="text-sm text-slate-600 max-w-md">
          {saturated
            ? "Toutes vos licences sont utilisées. Ajoutez une licence pour une prise en main supplémentaire simultanée."
            : `Vous pouvez encore ouvrir ${d.licensed - d.active_sessions} prise(s) en main en même temps.`}
        </p>
      </div>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat icon={Monitor} label="Machines" value={d.machines} id="ov-machines" />
        <Stat icon={Wifi} label="En ligne" value={d.online} id="ov-online" tone="text-green-600" />
        <Stat icon={PlugZap} label="Sessions en cours" value={d.in_session} id="ov-insession" tone="text-amber-600" />
        <Stat icon={Users} label="Comptes" value={d.technicians} id="ov-techs" />
        <Stat icon={History} label="Connexions aujourd'hui" value={d.sessions_today} id="ov-today" />
        <Stat icon={LifeBuoy} label="Assistances actives" value={d.active_support} id="ov-support" tone="text-sky-600" />
      </div>
    </div>
  );
};

const Audit = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/audit").then(({ data }) => setRows(data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!rows) return <Loader2 className="h-6 w-6 animate-spin text-slate-400" />;
  if (!rows.length) return <p className="text-sm text-slate-500" data-testid="audit-empty">Aucune activité enregistrée pour l'instant.</p>;
  return (
    <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 overflow-hidden" data-testid="admin-audit">
      {rows.map((r) => (
        <div key={r.audit_id} className="flex items-start gap-4 px-4 py-3 text-sm" data-testid="audit-row">
          <span className="w-44 flex-shrink-0 text-slate-500">{fmtDate(r.at)}</span>
          <span className="w-40 flex-shrink-0 font-medium">{ACTION_LABEL[r.action] || r.action}</span>
          <span className="w-32 flex-shrink-0 text-slate-600 truncate">{r.actor}</span>
          <span className="flex-1 text-slate-700 break-words">{r.detail}</span>
        </div>
      ))}
    </div>
  );
};

const Maintenance = () => {
  const [days, setDays] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/overview").then(({ data }) => setDays(String(data.retention_days))).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/retention", { retention_days: Number(days) });
      toast.success("Durée de conservation enregistrée");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  if (days === null) return <Loader2 className="h-6 w-6 animate-spin text-slate-400" />;
  return (
    <div className="rounded-lg border border-slate-200 p-6 max-w-xl" data-testid="admin-maintenance">
      <h3 className="font-semibold mb-1">Conservation des données</h3>
      <p className="text-sm text-slate-500 mb-5">L'historique des connexions, les commandes et le journal d'audit plus anciens que cette durée sont supprimés automatiquement.</p>
      <div className="flex items-end gap-3">
        <label className="text-sm">
          <span className="text-slate-600">Conserver pendant</span>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="mt-1 w-48" data-testid="retention-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[["30", "30 jours"], ["90", "90 jours"], ["180", "6 mois"], ["365", "1 an"], ["730", "2 ans"]].map(([v, l]) => (
                <SelectItem key={v} value={v}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <Button onClick={save} disabled={busy} data-testid="retention-save-btn">Enregistrer</Button>
      </div>
    </div>
  );
};

export default function Admin() {
  const [section, setSection] = useState("overview");
  const { billing } = useOutletContext() || {};
  const sections = SECTIONS.filter((x) => !x.module || !billing?.modules || billing.modules.includes(x.module));
  return (
    <div className="flex-1 min-h-0 flex" data-testid="admin-page">
      <aside className="w-72 flex-shrink-0 border-r border-slate-200 bg-[#F7F8FA]">
        <h1 className="px-6 pt-6 pb-4 text-2xl font-medium">Administration</h1>
        {sections.map(({ id, label, icon: Icon }) => (
          <button key={id} data-testid={`admin-nav-${id}`} onClick={() => setSection(id)}
            className={`w-full flex items-center gap-3 px-6 py-3 text-[15px] ${section === id ? "bg-slate-200/80 font-semibold" : "hover:bg-slate-100"}`}>
            <Icon className="h-5 w-5 text-slate-500" /> {label}
          </button>
        ))}
      </aside>
      <div className="flex-1 overflow-y-auto px-8 py-8">
        <h2 className="text-xl font-semibold mb-6">{SECTIONS.find((s) => s.id === section)?.label}</h2>
        {section === "overview" && <Overview />}
        {section === "audit" && <Audit />}
        {section === "automation" && <AutomationPanel />}
        {section === "maintenance" && <Maintenance />}
        {section === "branding" && <><DisplaySettings /><Branding /></>}
      </div>
    </div>
  );
}
